import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { instantAtFacilityTime } from "@alm/domain";
import { useState } from "react";
import { Plus } from "@/lib/icons";
import { MonthGrid } from "@/components/app/calendar/month-grid";
import { monthRange, weekRange, ymd } from "@/components/app/calendar/range";
import { WeekGrid } from "@/components/app/calendar/week-grid";
import { PageHeader } from "@/components/app/shell/page-header";
import { OffsetPaginationFooter } from "@/components/app/lists/offset-pagination-footer";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCalendarAnchor, useCalendarView } from "@/hooks/use-calendar-anchor";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import {
  listParam,
  optionalDay,
  optionalInt,
  optionalOneOf,
  optionalString,
  stringParams,
  KEEP_PLACE,
  optionalSearch,
} from "@/lib/search";
import { useCan } from "@/lib/policy";
import { useZone, useZoneFor } from "@/lib/facility/zone";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { EventBoard } from "./-components/event-board";
import { EventCancelDialog } from "./-components/event-cancel-dialog";
import { EventChip } from "./-components/event-chip";
import { EventDialog } from "./-components/event-dialog";
import {
  EMPTY_FILTERS,
  EventFiltersBar,
  eventFilterInput,
  type EventFilters,
} from "./-components/event-filters";
import { EventLine } from "./-components/event-line";
import { EventLegend } from "./-components/event-legend";
import { EventList } from "./-components/event-list";
import { defaultRange, resolveRange, type ListRange } from "./-components/list-range";
import { ListRangeBar } from "./-components/list-range-bar";
import {
  dayIn,
  EVENT_VIEW_KEY,
  EVENT_VIEWS,
  type EventRow,
  type EventView,
} from "./-components/event-model";
import { EventOutcomeDialog } from "./-components/event-outcome-dialog";
import { HappeningsFeed } from "./-components/happenings-feed";
import { useEveryEvent } from "./-components/use-every-event";
import { useEventMoves } from "./-components/use-event-moves";

const RANGE_PRESETS = [
  "next-month",
  "last-month",
  "next-30",
  "upcoming",
  "custom",
] as const satisfies readonly ListRange["preset"][];

type EventsSearch = {
  create?: string;
  leadId?: string;
  view?: EventView;
  q?: string;
  scope?: string;
  assignees?: string;
  statuses?: string;
  types?: string;
  range?: (typeof RANGE_PRESETS)[number];
  rfrom?: string;
  rto?: string;
  on?: string;
  page?: number;
  size?: number;
};

export const Route = createFileRoute("/_authenticated/_crm/crm/events/")({
  validateSearch: (search: Record<string, unknown>): EventsSearch => ({
    create: optionalString(search.create),
    leadId: optionalString(search.leadId),
    view: EVENT_VIEWS.find((view) => view === search.view),
    q: optionalSearch(search.q),
    ...stringParams(["scope", "assignees", "statuses", "types"], search),
    range: optionalOneOf(RANGE_PRESETS, search.range),
    rfrom: optionalDay(search.rfrom),
    rto: optionalDay(search.rto),
    on: optionalDay(search.on),
    page: optionalInt(search.page, 2, 100000),
    size: optionalInt(search.size, 5, 200),
  }),
  component: EventsPage,
});

const DAY = 86_400_000;
const BOARD_DAYS = 60;

/** The last instant of a calendar day on the building's clock. */
const endOfDayIn = (day: string, zone: string) =>
  new Date(instantAtFacilityTime(day, "23:59", zone).getTime() + 59_999);

/** Where the marketers are and will be: a calendar, a list, a board, and what has happened. */
function EventsPage() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const { create, leadId: linkedLeadId } = params;
  const navigate = useNavigate();
  const go = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<EventsSearch>) =>
    void go({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const zoneFor = useZoneFor();
  const zone = useZone(facilityId);
  // At the building on screen, whose events these are: a CRM role at another building plans none
  // here.
  const canWrite = useCan("crm:manage");
  const { view, setView } = useCalendarView(EVENT_VIEW_KEY, EVENT_VIEWS, params.view, (next) =>
    patch({ view: next }),
  );
  const { monthStart, weekStart, setMonthStart, setWeekStart } = useCalendarAnchor(
    params.on,
    (on) => patch({ on }),
  );
  const { text: searchText, change: setSearchText } = useSearchParamText(params.q, (q) =>
    patch({ q, page: undefined }),
  );
  const filters: EventFilters = {
    scope: (params.scope ?? EMPTY_FILTERS.scope) as EventFilters["scope"],
    assigneeIds: listParam(params.assignees),
    statuses: listParam(params.statuses) as EventFilters["statuses"],
    types: listParam(params.types) as EventFilters["types"],
    search: searchText,
  };
  const listRange: ListRange = params.range
    ? { preset: params.range, from: params.rfrom ?? "", to: params.rto ?? "" }
    : defaultRange();
  const page = params.page ?? 1;
  const pageSize = params.size ?? 25;
  const [creating, setCreating] = useState<{ day?: string; leadId?: string } | null>(() =>
    create ? { leadId: linkedLeadId } : null,
  );
  const [since] = useState(() => new Date(Date.now() - BOARD_DAYS * DAY));
  const moves = useEventMoves();
  const common = eventFilterInput({ ...filters, search: params.q ?? "" }, facilityId);
  const range = view === "week" ? weekRange(weekStart) : monthRange(monthStart);
  const calendar = useQuery({
    ...orpc.crm.events.calendar.queryOptions({ input: { ...common, ...range } }),
    enabled: view === "week" || view === "month",
    placeholderData: keepWhileSame({ facilityId: common.facilityId }),
  });
  const listDays = resolveRange(listRange);
  const list = useQuery({
    ...orpc.crm.events.list.queryOptions({
      input: {
        ...common,
        from: listDays.from ? instantAtFacilityTime(listDays.from, "00:00", zone) : undefined,
        to: listDays.to ? endOfDayIn(listDays.to, zone) : undefined,
        dir: "asc",
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
    }),
    enabled: view === "list",
    placeholderData: keepWhileSame({ facilityId: common.facilityId }),
  });
  const board = useEveryEvent({ ...common, from: since }, view === "board");
  const source = view === "list" ? list : view === "board" ? board : calendar;
  const rows: EventRow[] = source.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const open = (event: EventRow) =>
    void navigate({ to: "/crm/events/$eventId", params: { eventId: event.id } });
  const dayKey = (event: EventRow) => dayIn(event.startsAt, zoneFor(event.facilityId));
  const showDay = (day: string) => {
    patch({ range: "custom", rfrom: day, rto: day, page: undefined, view: "list" });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Events"
        meta="Health fairs, referral visits, open houses: where the marketers are, and what came of it."
        actions={
          canWrite ? (
            <Button onClick={() => setCreating({})}>
              <Plus className="size-4" />
              Plan event
            </Button>
          ) : null
        }
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={view} onValueChange={(next) => setView(next as EventView)}>
          <TabsList>
            <TabsTrigger value="month">Month</TabsTrigger>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="list">List</TabsTrigger>
            <TabsTrigger value="board">Board</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <EventFiltersBar
        filters={filters}
        onChange={(next) => {
          if (next.search !== filters.search) setSearchText(next.search);
          patch({
            scope: next.scope === EMPTY_FILTERS.scope ? undefined : next.scope,
            assignees: next.assigneeIds.join(",") || undefined,
            statuses: next.statuses.join(",") || undefined,
            types: next.types.join(",") || undefined,
            page: undefined,
          });
        }}
      />
      {view === "list" ? (
        <ListRangeBar
          range={listRange}
          onChange={(next) =>
            patch({
              range: next.preset === "this-month" ? undefined : next.preset,
              rfrom: next.preset === "custom" ? next.from || undefined : undefined,
              rto: next.preset === "custom" ? next.to || undefined : undefined,
              page: undefined,
            })
          }
        />
      ) : null}
      {view === "month" || view === "week" ? <EventLegend rows={rows} /> : null}

      {source.isError ? (
        <ReadFailed
          message="Could not load the events."
          error={source.error}
          onRetry={() => void source.refetch()}
          retrying={source.isFetching}
        />
      ) : view === "month" ? (
        <MonthGrid
          monthStart={monthStart}
          setMonthStart={setMonthStart}
          items={rows}
          dayKey={dayKey}
          renderLine={(event) => <EventLine event={event} zone={zoneFor(event.facilityId)} />}
          pending={calendar.isPending}
          emptyTitle="No events this month"
          onPickDay={(day) => showDay(ymd(day))}
        />
      ) : view === "week" ? (
        <WeekGrid
          weekStart={weekStart}
          setWeekStart={setWeekStart}
          items={rows}
          dayKey={dayKey}
          renderChip={(event) => (
            <EventChip
              event={event}
              zone={zoneFor(event.facilityId)}
              onPick={(picked) => showDay(dayKey(picked))}
            />
          )}
          pending={calendar.isPending}
          emptyTitle="No events this week"
          onPickDay={(day) => showDay(ymd(day))}
        />
      ) : view === "board" ? (
        <EventBoard rows={rows} canWrite={canWrite} onOpen={open} onMove={moves.move} />
      ) : view === "activity" ? (
        <HappeningsFeed
          facilityId={common.facilityId}
          mine={filters.scope === "mine"}
          assigneeId={filters.assigneeIds.length === 1 ? filters.assigneeIds[0] : undefined}
        />
      ) : (
        <EventList
          rows={rows}
          pending={list.isPending}
          canWrite={canWrite}
          onComplete={moves.setCompleting}
          onCancel={moves.setCancelling}
          footer={
            total > 0 ? (
              <OffsetPaginationFooter
                offset={(page - 1) * pageSize}
                pageSize={pageSize}
                total={total}
                noun="events"
                onOffsetChange={(offset) => {
                  const next = offset / pageSize + 1;
                  patch({ page: next > 1 ? next : undefined });
                }}
                onPageSizeChange={(next) =>
                  patch({ size: next === 25 ? undefined : next, page: undefined })
                }
              />
            ) : null
          }
        />
      )}

      {creating ? (
        <EventDialog
          event={null}
          defaults={{ day: creating.day, facilityId }}
          initialLeadId={creating.leadId}
          onClose={() => {
            setCreating(null);
            if (create) void navigate({ to: "/crm/events", search: {} });
          }}
        />
      ) : null}
      {moves.completing ? (
        <EventOutcomeDialog event={moves.completing} onClose={() => moves.setCompleting(null)} />
      ) : null}
      {moves.cancelling ? (
        <EventCancelDialog event={moves.cancelling} onClose={() => moves.setCancelling(null)} />
      ) : null}
    </div>
  );
}
